import { Alert, Button, Card, Descriptions } from 'antd'
import { useTranslation } from 'react-i18next'

import type { DeliveryAddressRoot } from '@repo/sdk'

interface ShippingAddressCardProps {
  /** Null when the buyer deleted the address the order pointed at (#87 — K-06 on old servers). */
  address: DeliveryAddressRoot | null
  /** Offered only while the parcel has not shipped; opens the address picker (#87). */
  onChange?: () => void
}

function formatFullAddress(address: DeliveryAddressRoot): string {
  if (address.addressLine2) {
    return `${address.addressLine1} ${address.addressLine2}`
  }
  return address.addressLine1
}

export function ShippingAddressCard({ address, onChange }: ShippingAddressCardProps) {
  const { t } = useTranslation()

  const extra = onChange ? (
    <Button size="small" onClick={onChange} data-testid="order-detail-change-address-btn">
      {t('pages.orders.changeShippingAddress.button')}
    </Button>
  ) : null

  if (!address) {
    return (
      <Card title={t('pages.orders.sections.shippingAddress')} bordered extra={extra}>
        <Alert
          type="warning"
          showIcon
          message={t('pages.orders.changeShippingAddress.missingAddress')}
        />
      </Card>
    )
  }

  return (
    <Card title={t('pages.orders.sections.shippingAddress')} bordered extra={extra}>
      <Descriptions column={1} size="small">
        <Descriptions.Item label={t('pages.orders.fields.recipientName')}>
          {address.recipientName}
        </Descriptions.Item>
        <Descriptions.Item label={t('pages.orders.fields.phoneNumber')}>
          {address.phoneNumber}
        </Descriptions.Item>
        <Descriptions.Item label={t('pages.orders.fields.postalCode')}>
          {address.postalCode}
        </Descriptions.Item>
        <Descriptions.Item label={t('pages.orders.fields.address')}>
          {formatFullAddress(address)}
        </Descriptions.Item>
      </Descriptions>
    </Card>
  )
}
